// gate.js: the first five seconds. The page opens on a dark cloud and one
// line of type. Press and hold anywhere (mouse, finger, or Space/Enter) and
// the cloud collapses: `hold` ramps 0 -> 1 over ~2.5 s, and letting go early
// lets it relax again. At 1 the star ignites: hold is locked at 1, the
// headline is blown apart, the screen takes a small kick, scroll unlocks and
// the film eases into its first beat. Scrolling, or "or just scroll", also
// ignites (a faster automatic hold), so nobody is ever stuck.
import { $, $$, clamp, lerp, damp } from './util.js';

const HOLD_TIME = 2.5;      // seconds of holding to ignite
const RELAX_TIME = 1.4;     // seconds for a full charge to relax when released
const AUTO_TIME = 0.9;      // seconds for the automatic (scroll / skip) charge
const RING_C = 276.46;      // 2 * pi * 44

export class Gate {
  constructor({ reduced, onIgnite, onGesture, pointer, home }) {
    this.reduced = reduced;
    this.home = home; // () => [x, y] CSS px of the protostar, or null
    this.overUI = false;
    this.moved = false;
    this.onIgnite = onIgnite;
    this.onGesture = onGesture;
    this.ptr = pointer;
    this.el = $('#gate');
    this.title = $('#gate-title');
    this.ring = $('#ring');
    this.ringFg = $('#ring-fg');
    this.state = 'loading'; // loading | gated | lit
    this.raw = 0;           // linear charge 0..1
    this.hold = 0;          // eased charge, what the world and the sound get
    this.holding = false;
    this.keyHold = false;
    this.auto = false;
    this.shownAt = 0;
    this.clock = 0;
    this.buzz = 0;
    this.litAt = -1;
    this.words = [];
    this.ringXY = [innerWidth / 2, innerHeight * 0.45];
    this.ringShown = false; this.ringPressed = false; this.ringCls = '';
    this.bind();
  }

  bind() {
    const interactive = (t) => t && t.closest && t.closest('a, button, input, label, .chrome');
    addEventListener('pointermove', (e) => {
      if (e.pointerType === 'mouse') { this.moved = true; document.body.classList.add('has-mouse'); }
      this.overUI = !!interactive(e.target);
    }, { passive: true });
    addEventListener('pointerdown', (e) => {
      if (this.onGesture) this.onGesture(e);
      if (this.state !== 'gated' || interactive(e.target) || e.button > 0) return;
      this.holding = true;
      this.ringXY = [e.clientX, e.clientY];
      this.el.classList.add('holding');
    });
    const release = () => {
      if (!this.holding) return;
      this.holding = false;
      if (this.state === 'gated') this.el.classList.remove('holding');
    };
    addEventListener('pointerup', release);
    addEventListener('pointercancel', release);
    addEventListener('blur', () => { release(); this.keyHold = false; });
    // no long-press menus / selection while holding on touch screens
    addEventListener('contextmenu', (e) => { if (this.state !== 'lit') e.preventDefault(); });
    addEventListener('touchstart', (e) => { if (this.state === 'gated' && !interactive(e.target) && e.cancelable) e.preventDefault(); }, { passive: false });
    addEventListener('keydown', (e) => {
      if (this.state !== 'gated' || (e.target && /INPUT|TEXTAREA/.test(e.target.tagName))) return;
      if (e.key === ' ' || e.key === 'Enter') {
        // a focused control keeps its own keys (Enter on links and buttons, Space on buttons: Sound, "or just scroll")
        if (e.target && e.target.closest && (e.target.closest('button') || (e.key === 'Enter' && e.target.closest('a')))) return;
        e.preventDefault();
        if (this.onGesture) this.onGesture(e);
        if (!e.repeat) { this.keyHold = true; this.el.classList.add('holding'); const r = $('#prompt').getBoundingClientRect(); this.ringXY = [r.left + r.width / 2, r.top - 70]; }
      } else if (['ArrowDown', 'PageDown', 'End'].includes(e.key)) {
        e.preventDefault();
        if (this.onGesture) this.onGesture(e);
        this.autoIgnite();
      }
    });
    addEventListener('keyup', (e) => {
      if (e.key === ' ' || e.key === 'Enter') { this.keyHold = false; if (this.state === 'gated' && !this.holding) this.el.classList.remove('holding'); }
    });
    // scrolling ignites too
    addEventListener('wheel', (e) => { if (this.state === 'gated' && e.deltaY > 4) this.autoIgnite(); }, { passive: true });
    let ts = null;
    addEventListener('touchstart', (e) => { if (e.touches[0]) ts = { y: e.touches[0].clientY, t: performance.now() }; }, { passive: true });
    addEventListener('touchmove', (e) => {
      if (!ts || this.state !== 'gated' || !e.touches[0]) return;
      const dy = ts.y - e.touches[0].clientY;
      if (dy > 60 && performance.now() - ts.t < 500) { ts = null; this.holding = false; this.autoIgnite(); }
    }, { passive: true });
    $('#just-scroll').addEventListener('click', (e) => { e.preventDefault(); if (this.onGesture) this.onGesture(e); this.autoIgnite(); });
  }

  // measure each headline word's offset from the centre (for the pull)
  measure() {
    const tr = this.title.getBoundingClientRect();
    const cx = tr.left + tr.width / 2, cy = tr.top + tr.height / 2;
    this.words = $$('.w', this.title).map((w) => {
      const r = w.getBoundingClientRect();
      return { el: w, dx: r.left + r.width / 2 - cx, dy: r.top + r.height / 2 - cy, tf: '' };
    });
  }

  show() {
    this.state = 'gated';
    this.shownAt = this.clock;
    this.measure();
    requestAnimationFrame(() => this.el.classList.add('on'));
  }

  autoIgnite() {
    if (this.state !== 'gated') return;
    this.auto = true;
    this.el.classList.add('holding');
  }

  // skip the whole gesture (deep links, tests, no-JS-hold visitors)
  forceLit() {
    if (this.state === 'lit') return;
    this.raw = 1; this.hold = 1;
    this.ignite(true);
  }

  ignite(silent = false) {
    this.state = 'lit';
    this.litAt = this.clock;
    this.holding = false; this.keyHold = false; this.auto = false;
    this.el.classList.add('gone');
    this.el.classList.remove('holding');
    this.title.style.transition = 'transform 1.4s cubic-bezier(.16,1,.3,1)';
    this.title.style.transform = this.reduced ? '' : 'translate3d(0, -40px, 0) scale(1.06)';
    for (const w of this.words) {
      const k = 0.5 + 0.02 * Math.abs(w.dx) / 10;
      w.el.style.transform = this.reduced ? '' : `translate3d(${(w.dx * 0.28).toFixed(1)}px, ${(w.dy * 0.4).toFixed(1)}px, 0) scale(${(1 + 0.04 * k).toFixed(3)})`;
      w.el.style.transition = 'opacity .8s ease, transform 1.4s cubic-bezier(.16,1,.3,1)';
      w.el.style.opacity = '0';
    }
    if (!silent && !this.reduced && this.ringShown) {
      this.ring.classList.add('burst');
      this.ring.style.transform = `translate3d(${this.ringXY[0].toFixed(1)}px, ${this.ringXY[1].toFixed(1)}px, 0) scale(2.6)`;
      setTimeout(() => this.ring.classList.remove('on', 'pressed'), 30);
    } else this.ring.classList.remove('on', 'pressed');
    if (!silent) { try { navigator.vibrate && navigator.vibrate([24, 40, 80]); } catch (e) { /* no haptics */ } }
    this.onIgnite(silent);
  }

  tick(dt) {
    this.clock += dt;
    if (this.state !== 'gated') return;
    const late = this.clock - this.shownAt > 4;
    if (late && !this.el.classList.contains('late')) this.el.classList.add('late');
    const pressing = this.holding || this.keyHold;
    const rate = this.auto ? 1 / AUTO_TIME : 1 / (this.reduced ? HOLD_TIME * 0.6 : HOLD_TIME);
    if (pressing || this.auto) this.raw = Math.min(1, this.raw + dt * rate);
    else this.raw = Math.max(0, this.raw - dt / RELAX_TIME);
    // eased: slow to start (the cloud resists), decisive at the end
    const r = this.raw;
    this.hold = r * r * (3 - 2 * r) * 0.85 + r * 0.15;
    // the ring: before the press it waits on the protostar (or trails the
    // mouse); pressed, it sits under the finger and fills as the charge builds
    let tx, ty, k = 10;
    if (this.holding) { tx = this.ptr.cx; ty = this.ptr.cy; k = 40; }
    else if (this.keyHold) { tx = this.ringXY[0]; ty = this.ringXY[1]; }
    else if (this.moved && this.ptr.mouse && this.ptr.p.inside) { tx = this.ptr.cx; ty = this.ptr.cy; k = 16; }
    else { const h = this.home && this.home(); tx = h ? h[0] : innerWidth / 2; ty = h ? h[1] : innerHeight * 0.45; k = 6; }
    const first = !this.ringShown;
    if (first) { this.ringXY[0] = tx; this.ringXY[1] = ty; }
    this.ringXY[0] = damp(this.ringXY[0], tx, k, dt); this.ringXY[1] = damp(this.ringXY[1], ty, k, dt);
    const show = this.clock - this.shownAt > 1.0 && !this.overUI && !this.auto;
    if (show !== this.ringShown) { this.ring.classList.toggle('on', show); this.ringShown = show; }
    const pressed = pressing && this.raw > 0.001;
    if (pressed !== this.ringPressed) { this.ring.classList.toggle('pressed', pressed); this.ringPressed = pressed; }
    const sc = pressed ? 1 - 0.22 * this.hold : 1;
    this.ring.style.transform = `translate3d(${this.ringXY[0].toFixed(1)}px, ${this.ringXY[1].toFixed(1)}px, 0) scale(${sc.toFixed(3)})`;
    this.ringFg.style.strokeDashoffset = String((RING_C * (1 - this.raw)).toFixed(2));
    // the headline is pulled toward its centre as the cloud collapses
    if (!this.reduced) {
      const k = this.hold * this.hold;
      const tf = k > 0.001 ? `translate3d(0, ${(-26 * k).toFixed(1)}px, 0) scale(${(1 - 0.12 * k).toFixed(4)})` : '';
      if (tf !== this.titleTf) { this.title.style.transform = tf; this.titleTf = tf; }
    }
    this.title.style.opacity = String((1 - 0.5 * this.hold).toFixed(3));
    // haptics on Android: pulses that quicken as the charge builds
    if (pressing && navigator.vibrate) {
      this.buzz -= dt;
      if (this.buzz <= 0) { this.buzz = lerp(0.42, 0.1, this.raw); try { navigator.vibrate(Math.round(lerp(6, 16, this.raw))); } catch (e) { /* ignore */ } }
    }
    if (this.raw >= 1) this.ignite();
  }

  get igniteT() { return this.litAt < 0 ? -1 : this.clock - this.litAt; }
}

export { clamp };
